import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import moment from 'moment';
import { CustomHttpParams } from '../shared/loader/custom-httpParam';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { CommonService } from '../shared/services/common.service';
import { UtilizationSettings, UtilizationSettingType } from '../home/site-dashboard/site-settings/site-utilization-settings/site-utilization-settings.interface';
import { DeviceFactory } from '../home/site-dashboard/device/device-factory/device-factory';

@Injectable({
	providedIn: 'root'
})
export class SitesService {

	route = '/site/';
	imDeviceRoute = '/imDevice/';

	specialFolderId = 1;
	sctInventorySiteId = 48;
	sctCustomerId = 1;
	defaultMapLocation = {
		latitude: 28.623421,
		longitude: -81.212219,
	};

	private siteAnalyticsData = {};
	public activeSiteAnalyticsData:Subject<any> = new Subject();

	private siteUtilizationSettingsObs = new BehaviorSubject<Object>({});
	siteUtilizationSettingsData = this.siteUtilizationSettingsObs.asObservable();

	constructor(
		private httpClient: HttpClient,
		private commonService: CommonService,
	) { }

	setUtilizationSettingsCurrentSite(UtilizationSettings) {
		this.siteUtilizationSettingsObs.next(UtilizationSettings);
	}

	getCustomerSites(customerId, options) {
		return this.httpClient.post(this.route + 'getCustomerSites', {customerId, options}, {
			observe: "body"
		});
	}

	getSites(siteIds: number[], options) {
		return this.httpClient.post(this.route + 'getSites', {siteIds, options}, {
			observe: "body"
		});
	}

	getSitesFirmwareData() {
		return this.httpClient.get(this.route + 'getSitesFirmwareData', {
			observe: "body"
		});
	}

	getUpToDateSiteFw(siteId) {
		return this.httpClient.get(this.route + 'getUpToDateSiteFw', {
			observe: "body",
			params: {siteId}
		});
	}

	getSiteFwUpdateRequests(type, siteId) {
		return this.httpClient.get(this.route + 'getSiteFwUpdateRequests', {
			observe: "body",
			params: {type, siteId}
		});
	}

	getSmartRebatesSitesInfo() {
		return this.httpClient.get(this.route + 'getSmartRebatesSitesInfo', {
			observe: "body"
		});
	}

	pushFirmwareUpdate(siteId, type, firmwareVersion) {
		return this.httpClient.post(this.route + 'pushFirmwareUpdate', {siteId, type, firmwareVersion}, {
			observe: "body"
		});
	}

	abortFirmwareUpdate(siteId) {
		return this.httpClient.post(this.route + 'abortFirmwareUpdate', {siteId}, {
			observe: "body"
		});
	}

	deleteSite(siteId) {
		return this.httpClient.delete(this.route + 'deleteSite', {
			observe: "body",
			body: {siteId}
		});
	}

	saveSite(site) {
		return this.httpClient.post(this.route + 'saveSite', site, {
			observe: "body"
		});
	}

	getSiteDevicesCount(siteId) {
		return this.httpClient.get(this.route + 'getSiteDevicesCount', {
			observe: "body",
			params: Object.assign({siteId}, new CustomHttpParams({noUIBlock: true}))
		});
	}

	getReportsData(siteId, deviceType = [DeviceFactory.types.iotah]) {
		return this.httpClient.get(this.route + 'getReportsData', {
			observe: "body",
			params: {siteId, deviceType: JSON.stringify(deviceType), ...new CustomHttpParams({noUIBlock: true})}
		});
	}

	getDailyUtilizationData(siteId: number, fromDate: number, toDate: number, sct_device_types: number[]){
		return this.httpClient.post(this.route + 'getDailyUtilizationData', {siteId, fromDate, toDate, sct_device_types}, {
			observe: "body"
		});
	}

	getDailyTruckFleetActivityMetrics(siteId: number, fromDate: number, toDate: number, sct_device_types: number[], criteria: { truck_type?: string , truck_manufacturer?: string}){
		return this.httpClient.post(this.route + 'getDailyTruckFleetActivityMetrics', {siteId, fromDate, toDate, sct_device_types, criteria}, {
			observe: "body"
		});
	}

	getAhDailyUtilizationData(siteId: number, fromDate: number, toDate: number, sct_device_types: number[]){
		return this.httpClient.get(this.route + 'getAhDailyUtilizationData', {
			observe: "body",
			params: {
				siteId: siteId.toString(),
				fromDate: fromDate.toString(),
				toDate: toDate.toString(),
				sct_device_types: JSON.stringify(sct_device_types),
			}
		});
	}

	getUtilizationScoreData(siteId: number, sct_device_types: number[], options?: {relative_utilization_chart?: boolean}) {
		return this.httpClient.post(this.route + 'getUtilizationScoreData', {siteId, sct_device_types, options}, {
			observe: "body"
		});
	}

	getUtilizationScoreDataTruckReport(siteId: number, sct_device_types: number[], options?: {relative_utilization_chart?: boolean}) {
		return this.httpClient.post(this.route + 'getUtilizationScoreDataTruckReport', {siteId, sct_device_types, options}, {
				observe: "body"
		});
	}
	
	getChargersUtilizationData(siteId: number, macAddress: string, filters?: { selectedVoltage: string, selectedManufacturer: string, selectedPeriod?: string }, getHeatData?: boolean) {
		return this.httpClient.post(this.route + 'getChargersUtilizationData', { siteId, macAddress, filters, getHeatData }, {
			observe: "body"
		});
	}

	getDailyChargerUsageReport(from: number, to: number, siteId: number) {
		return this.httpClient.get(this.route + 'getDailyChargerUsageReport', {
			observe: "body",
			params: {from: from.toString(), to: to.toString(), siteId: siteId.toString()}
		});
	}

	getDailyBatteriesUsageReport(from: number, to: number, siteId: number, options?: { batteryVoltage?: string, batteryCapacity?: string, mac_address?: string, batteryVintage?: string[] }) {
		return this.httpClient.post(this.route + 'getDailyBatteriesUsageReport', {from, to, siteId, options}, {
			observe: "body"
		});
	}

	getMonthlyQuarterlyUsageReport(siteId: number, year: number, deviceTypes: number[], options?: any) {
		return this.httpClient.post(this.route + 'getMonthlyQuarterlyUsageReport', {siteId, year, deviceTypes, options}, {
			observe: "body"
		});
	}

	getDailyHourlyChargerUsageData(siteId: number, options: {from_date: number, to_date: number, period_type: string}) {
		return this.httpClient.post(this.route + 'getDailyHourlyChargerUsageData', {siteId, options}, {
			observe: "body"
		});
	}

	getUsageChart(siteId: number, options: {is_daily: boolean, year: number, sct_devices_types: number[], mac_address?: string}) {
		return this.httpClient.post(this.route + 'getUsageChart', {siteId, options}, {
			observe: "body"
		});
	}

	getEndVoltageChart(siteId: number, event_type: string, options: { from_date: number, to_date: number, battery_voltage: string, battery_capacity: string, battery_vintage: string[] }) {
		return this.httpClient.post(this.route + 'getEndVoltageChart', {siteId, event_type, options}, {
			observe: "body"
		});
	}

	getBatteryDistributionData(siteId: number, chartType: string, options: {from_date: number, to_date: number, battery_voltage: string, battery_capacity: string, battery_vintage: string[], battery_manufacturer: string }) {
		return this.httpClient.get(this.route + 'getBatteryDistributionData', {
			observe: "body",
			params: {
				site_id: siteId,
				chart_type: chartType,
				from_date: options.from_date,
				to_date: options.to_date,
				battery_voltage: options.battery_voltage,
				battery_capacity: options.battery_capacity,
				battery_vintage: options.battery_vintage,
				battery_manufacturer: options.battery_manufacturer
			}
		});
	}

	addSitesConnectivityStatusIcons(sites) {

		let icons = {
			green: '/images/site-connectivity/green-dot.png',
			yellow: '/images/site-connectivity/yellow-dot.png',
			red: '/images/site-connectivity/red-dot.png',
		};

		sites.map((site) => {

			let minutesDiff = moment().diff(moment(site.site_last_connect_time*1000), 'minutes');

			if(minutesDiff <= 30)
				site.icon = icons.green;
			else if(minutesDiff <= 12*60)
				site.icon = icons.yellow;
			else
				site.icon = icons.red;
		});
	}

	getSitesAnalyticsDataCached(siteIds) {
		const existsData = [];
		let notExistsSites = [];

		for(const siteID of siteIds) {
			if (this.siteAnalyticsData[siteID] !== undefined) {
				existsData.push(this.siteAnalyticsData[siteID]);
			} else {
				notExistsSites.push(siteID);
			}
		}

		if(!notExistsSites.length) {
			this.activeSiteAnalyticsData.next(existsData);
			return new Observable((observer) => {
				observer.next(existsData);
				observer.complete();
			});
		}

		this.httpClient.post(this.route + 'getSitesAnalyticsData', {siteIds: notExistsSites}, {
			observe: "body"
		}).subscribe((data: any) => {
			for(const siteID in data) {
				const siteId = +siteID;
				if (!data[siteId])
					continue;

				const siteData = this.decompressSiteAnalyticsData(data[siteId]);
				existsData.push(siteData);
				this.siteAnalyticsData[siteId] = siteData;
				notExistsSites = notExistsSites.filter((item) => {
					return item !== siteId
				});
			}
			for(const siteID of notExistsSites) {
				this.siteAnalyticsData[siteID] = {};
			}
			this.activeSiteAnalyticsData.next(existsData);
			return new Observable((observer) => {
				observer.next(existsData);
				observer.complete();
			});
		});
	}

	private decompressSiteAnalyticsData(data) {
		return {
			ioTAh_devices: this.commonService.decompress(data.i, 'siteInfoBox'),
			chargLink_devices: this.commonService.decompress(data.c, 'siteInfoBox'),
			battLink_devices: this.commonService.decompress(data.b, 'siteInfoBox'),
			iotemeter_devices: this.commonService.decompress(data.im, 'siteInfoBox')
		};
	}

	// Analytics-source comparison diagnostic: same endpoint as getSitesAnalyticsDataCached, but
	// uncached and forcing one table via summarySource - only honored server-side for
	// analytics_source_comparison. Backs the sites-map panel that shows both sources side by side,
	// so it stays a real comparison whichever source is selected.
	getSitesAnalyticsDataForSource(siteIds, summarySource: 'old' | 'new') {
		return this.httpClient.post(this.route + 'getSitesAnalyticsData', {siteIds, summarySource}, {
			observe: "body"
		}).pipe(map((data: any) => {
			const result = [];
			for (const siteID in data) {
				if (!data[siteID])
					continue;

				result.push(this.decompressSiteAnalyticsData(data[siteID]));
			}
			return result;
		}));
	}

	getUserSitesInfo(options) {
		return this.httpClient.post(this.route + 'getUserSitesInfo', {options}, {
			observe: "body"
		});
	}

	getMaintenanceReport(siteId, options?) {
		return this.httpClient.post(this.route + 'getMaintenanceReport', {siteId, options}, {
			observe: "body"
		});
	}

	getSiteDevicesWarranty(siteId) {
		return this.httpClient.get(this.route + 'getSiteDevicesWarranty', {
			observe: "body",
			params: {siteId}
		});
	}

	setSiteEndVoltageLevels(siteId, siteEndVoltageLevels) {
		return this.httpClient.post(this.route + 'setSiteEndVoltageLevels', {siteId, siteEndVoltageLevels}, {
			observe: "body"
		});
	}

	getSiteDownloadStatus(siteId) {
		return this.httpClient.get(this.route + 'getSiteDownloadStatus', {
			observe: "body",
			params: {siteId}
		});
	}

	getEnterpriseSitesInfo(enterpriseId) {
		return this.httpClient.post(this.route + 'getEnterpriseSitesInfo', {enterpriseId}, {
			observe: "body"
		});
	}

	getIMEnterpriseSitesInfo(enterpriseId) {
		return this.httpClient.post(this.route + 'getIMEnterpriseSitesInfo', { enterpriseId }, {
			observe: "body"
		});
	}

	downloadConsumptionsReport(from: number, to: number, siteIds: number[], selectedStartDate: number) {
		return this.httpClient.get(this.route + 'get-sr-consumption-reports', {
			observe: "body",
			params: { siteIds: JSON.stringify(siteIds), from: from.toString(), to: to.toString(), selectedStartDate: selectedStartDate.toString() }
		});
	}

	registrationPurposes(siteId: number) {
		return this.httpClient.post('reporting/registrationPurposes', { siteId });
	}

	getSitesWithCustomerInfo() {
		return this.httpClient.get(this.route + 'getSitesWithCustomerInfo', {
			observe: "body"
		});
	}

	getAllSiteGroups(getSiteInfo = true) {
		return this.httpClient.get(this.route + 'getAllSiteGroups', {
			observe: "body",
			params: {
				getSiteInfo
			}
		});
	}

	getSiteHistory(filters: {siteId: number, customerId: number, fromTime: string, toTime: string}) {
		return this.httpClient.get(this.route + 'getSiteHistory', {
			observe: "body",
			params: filters
		});
	}

	getBRMGroupsData(filters: {siteId: number, token?: string, fromDate: string, toDate: string, isHistory: boolean, publicPageReq: boolean, deviceId?: string}, noUiBlock = true) {
		return this.httpClient.post(this.route + 'getBRMGroupsData', { ...filters }, {
			observe: "body",
			params: new CustomHttpParams({noUIBlock: noUiBlock})
		});
	}

	getSitePickLogs(filters: {siteId: number, fromDate: string, toDate: string, isMissed: boolean}) {
		return this.httpClient.post(this.route + 'getSitePickLogs', { ...filters })
	}

	checkBuzzer(options: {siteId: number, customerId: number}) {
		return this.httpClient.get(this.route + 'checkBuzzer', {
			observe: "body",
			params: options
		});
	}

	playBuzzer() {
		const audio = new Audio();
		audio.src = '/sctportal/audio/buzz.mp3';
		audio.load();
		audio.play().catch(error => console.log('Playback error:', error));
	}

	enableBuzzer() {
		const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
		if (audioCtx.state === 'suspended') {
			audioCtx.resume().then(() => {
				console.log('Audio context resumed');
			});
		}
	}

	saveSiteGroups(groupInfo) {
		return this.httpClient.post(this.route + 'saveSiteGroups', { group: groupInfo })
	}

	deleteSiteGroups(groupId) {
		return this.httpClient.delete(this.route + 'deleteSiteGroups', {
			observe: "body",
			params: {
				groupId
			}
		})
	}

	getBRMDeviceGroups(siteId: number) {
		return this.httpClient.get(this.route + 'getBRMDeviceGroups', {
			observe: "body",
			params: {
				siteId
			}
		});
	}

	saveBRMDeviceGroup(groupText: string, siteId: number) {
		return this.httpClient.post(this.route + 'saveBRMDeviceGroup', { groupText, siteId });
	}

	deleteBRMDeviceGroup(groupId: number) {
		return this.httpClient.delete(this.route + 'deleteBRMDeviceGroup', {
			observe: "body",
			params: {
				groupId
			}
		});
	}

	saveSiteUtilizationSettings(siteId: number, settingType: UtilizationSettingType, utilizationSettings: UtilizationSettings, workingDays?: number[]) {
		return this.httpClient.post(this.route +'saveSiteUtilizationSettings', { siteId, settingType, utilizationSettings, workingDays });
	}

	getSiteUtilizationSettings(siteId: number) {
		return this.httpClient.get(this.route +'getSiteUtilizationSettings', { observe: "body",
			params: {
				siteId
			}
		});
	}

	getSiteDevicesAnalyticsData(siteId: number) {
		return this.httpClient.get(this.route +'getSiteDevicesAnalyticsData', { observe: "body",
			params: {
				siteId
			}
		});
	}

	getDailyRtData(from: number, to: number, mac_address: string | null, sct_device_types: number[], site_id: number=0, study_id: number = 0, options?: any) {
		return this.httpClient.get(this.route + 'getDailyRtData', {
			observe: "body",
			params: {
				from: from,
				to: to,
				mac_address: mac_address,
				sct_device_types: sct_device_types,
				site_id: site_id,
				study_id: study_id,
				options: JSON.stringify(options || {})
			}
		});
	}

	getBatteryExceptionReports(siteId: number) {
		return this.httpClient.get(this.route +'getBatteryExceptionReports', { observe: "body",
			params: { siteId }
		});
	}

	getChargerExceptionReports(siteId: number) {
		return this.httpClient.get(this.route + 'getChargerExceptionReports', {
			observe: "body",
			params: { siteId }
		});
	}

	getTruckExceptionReports(siteId: number) {
		return this.httpClient.get(this.route + 'getTruckExceptionReports', {
			observe: "body",
			params: { siteId }
		});
	}

	getBatteryUsagePerformanceReports(siteId: number) {
		return this.httpClient.get(this.route +'getBatteryUsagePerformanceReports', { observe: "body",
			params: { siteId }
		});
	}

	getBrmSiteSettings(siteId: number) {
		return this.httpClient.get(this.route + 'get-brm-site-settings', {
			observe: "body",
			params: {
				siteId
			}
		});
	}

	saveBrmSiteSettings(siteId: number, settings: any) {
		return this.httpClient.post(this.route +'save-brm-site-settings', { siteId, settings });
	}

	getBrmSimulationData(siteId: number) {
		return this.httpClient.get(this.route +'get-brm-simulation-data', {
			observe: "body",
			params: {
				siteId,
			}
		})
	}

	getPotentiallyMalfunctionedChargers(siteId: number, options?: { withCleared?: boolean }) {
		return this.httpClient.get(this.route +'get-potentially-malfunctioned-devices', {
			observe: "body",
			params: {
				siteId,
				options: JSON.stringify(options || {})
			}
		})
	}

	unmarkMalfunctionedDevice(macAddress: string, siteId: number) {
		return this.httpClient.post(this.route +'unmark-malfunctioned-device', { siteId, macAddress }, {
			observe: "body"
		});
	}

	updateBrmSimulationData(siteId: number, devicesData: any[]) {
		return this.httpClient.post(this.route +'update-brm-simulation-data', { siteId, devicesData }, {
			observe: "body"
		});
	}

	getIotemeterCustomersAndSites() {
		return this.httpClient.get(this.route +'get-iotemeter-customers-sites', {
			observe: 'body'
		});
	}

	getSitePanelIds(siteId: number, macAddress: string | null = null) {
		return this.httpClient.get(this.route + 'panel-ids', {
			observe: "body",
			params: {siteId, macAddress: macAddress || '', ...new CustomHttpParams({noUIBlock: true})}
		});
	}

	insertPanel(siteId: number, macAddress: string | null, panel_id: string) {
		return this.httpClient.post(this.route + 'insert-panel-id', { siteId, macAddress, panel_id }, {
			observe: "body"
		});
	}

	getSitePanels(siteId: number, year: number, quarter: string) {
		return this.httpClient.get(this.route + 'site-panels', {
			observe: "body",
			params: {siteId, year, quarter}
		});
	}

	deletePanel(siteId: number, panel_id: string) {
		return this.httpClient.post(this.route + 'delete-panel-id', { siteId, panel_id }, {
			observe: "body"
		});
	}

	getSiteReportsIotemeterData(site_id: number) {
		return this.httpClient.get(this.imDeviceRoute +'getReportsData', { observe: "body",
			params: {
				site_id
			}
		});
	}

	regenerateBrmPublicToken(siteId: number, token: string) {
		return this.httpClient.post(this.route + 'regenerateBrmPublicToken', { siteId, token }, {
			observe: 'body'
		});
	}

	requestRevealPassword(site_id: number, customer_id: number, request_type: string, device_id?: string) {
		const requestData: any = { site_id, customer_id, request_type };
		if (device_id) {
			requestData.device_id = device_id;
		}
		return this.httpClient.post('/admin/password-reveal-requests/create', requestData, {
			observe: "body"
		});
	}

	getDailyAvgBatteriesUsageReport(from: number, to: number, siteId: number, options?: { batteryVoltage?: string, batteryCapacity?: string, batteryVintage?: string[] }) {
		return this.httpClient.post(this.route + 'getDailyAvgBatteriesUsageReport', { from, to, siteId, options }, {
			observe: "body"
		});
	}

	getIotemeterEnergyConsumption(criteria: {
		siteId: number,
		year: string | number,
		periodType: string,
		quarter?: string,
		month?: string | number,
		deviceType: string | number,
		mac_address?: string
	}) {
		return this.httpClient.get(`${this.route}get-iotemeter-energy-consumption`, { params: { ...criteria } });
	}

	getIotemeterDailyEnergyReports(criteria: { from: number, to: number, siteId: number, deviceType: string | number }) {
		return this.httpClient.get(`${this.route}get-iotemeter-daily-energy-reports`, { params: { ...criteria } });
	}

	getImExceptionReports(criteria: { siteId: number, period: string }) {
		return this.httpClient.get(`${this.route}getImExceptionReports`, { params: { ...criteria } });
	}

	getMeterEnergyReport(criteria: { siteId: number, year: string | number, periodType: string, quarter: string, month: string | number, deviceType: string | number }) {
		return this.httpClient.get(`${this.route}get-meter-energy-report`, { params: { ...criteria } });
	}

	setBRMDeviceMaintenance(siteId: number, macAddress: string, inMaintenance: boolean) {
		return this.httpClient.post(`${this.route}set-brm-device-maintenance`, { siteId, macAddress, inMaintenance });
	}

}
